import {runInAction} from 'mobx'
import React from 'react'
import {Time} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'
import {GanttDraft} from '@extensions/gantt-draft'
import {globals} from '@extensions/globals-base'
import {DeadlineCtl} from '@extensions/sticker/controllers/deadline-ctl'
import {Sticker, stickerCtl} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {TimelineNewStore} from './timeline-new-store'
import {Point} from './types'

export type BoxDragTarget = 'box' | 'left' | 'right'

export class BoxHelper {
  private startPoint: Point
  private currentPoint: Point
  private target: BoxDragTarget
  private originalWorkingDays: number

  constructor(private readonly store: TimelineNewStore) {
    this.startBoxDrag = this.startBoxDrag.bind(this)
    this.boxMouseMove = this.boxMouseMove.bind(this)
    this.boxMouseUp = this.boxMouseUp.bind(this)
  }

  startBoxDrag(
    target: BoxDragTarget,
    path: string,
    hubId: DataObjectId,
    e: React.MouseEvent
  ): void {
    e.stopPropagation()
    e.preventDefault()

    const box = Sync.getCurrentSandbox()
    const tag = stickerCtl.Deadline.getSticker(box)
    const hub = box.getHub(hubId)
    if (!PermissionsChecker.checkEditTaskStickers(hub, tag)) {
      return
    }

    const [start, end] = this.getInitialRange(hubId)
    if (!start || !end) {
      return
    }

    const p: Point = {x: e.clientX, y: e.clientY}
    this.startPoint = p
    this.currentPoint = p
    this.target = target
    this.originalWorkingDays = Time.workingDaysBetween(start, end)
    this.subscribe()

    runInAction(() => {
      this.store.state.draggingPath = path
      this.store.state.draggingHubId = hubId
      this.store.state.draggingStart = start
      this.store.state.draggingEnd = end
      this.store.state.draggingTarget = target

      // todo fix for cases when hovering a button
      const cursor = target === 'box' ? '!cursor-grabbing' : '!cursor-ew-resize'
      this.store.setCursorClass(cursor)
    })
  }

  private boxMouseMove(e: MouseEvent): void {
    // no stopPropagation here for manual diagram rows hover to work properly
    e.preventDefault()

    let x = e.clientX
    let y = e.clientY
    const diagramEl = this.store.state.diagramEl
    if (diagramEl) {
      const {top, bottom, left, right} = diagramEl.getBoundingClientRect()
      x = Math.min(Math.max(x, left), right)
      y = Math.min(Math.max(y, top), bottom)
    }
    this.currentPoint = {x, y}

    if (!this.isDragging) {
      return
    }

    const [start, end] = this.getInitialRange()
    if (!start || !end) {
      return
    }

    const [newStart, newEnd] = this.translateTime(start, end)

    runInAction(() => {
      this.store.state.draggingStart = newStart
      this.store.state.draggingEnd = newEnd
    })
  }

  private boxMouseUp(e: MouseEvent): void {
    e.stopPropagation()
    e.preventDefault()

    this.unsubscribe()

    const {draggingHubId: hubId, itemDataRegistry: registry} = this.store.state
    runInAction(() => {
      this.store.setCursorClass('')
      this.store.state.draggingPath = undefined
      this.store.state.draggingHubId = undefined
    })

    if (!hubId || !(hubId in registry)) {
      return
    }

    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(hubId)
    if (!hub) {
      return
    }

    // Tasks w/ dependencies can be dragged, but result is not applied
    const blockedPoints = DeadlineCtl.getBlockedPoints(hub)
    if (blockedPoints.includes('start') && this.isBoth) {
      return
    }

    const {current} = Sticker.getDeadline(hub)
    if (current?.startDate && current?.deadline) {
      const [startDateTs, endDateTs] = this.translateTime(
        current.startDate,
        current.deadline
      )
      if (startDateTs !== current.startDate || endDateTs !== current.deadline) {
        const startDate = Time.toLocalDate(startDateTs)
        const deadline = Time.toLocalDate(endDateTs)

        // Keep hours, minutes, and seconds
        Time.applyTimeOffset(startDate, Time.toLocalDate(current.startDate))
        Time.applyTimeOffset(deadline, Time.toLocalDate(current.deadline))

        const newStart = Time.toTimestamp(startDate)
        const newEnd = Time.toTimestamp(deadline)
        this.updateItemDataRegistry(hubId, newStart, newEnd)
        this.updateDeadlineSticker(hub, newStart, newEnd)
      }
    } else if (current?.deadline) {
      const [newStartDate, newDeadline] = this.translateTime(
        current.deadline,
        current.deadline
      )
      const dtDeadline = newDeadline - current.deadline
      const dtStartDate = newStartDate - current.deadline
      if (this.isBoth) {
        // Keep only deadline in sticker w/o start date
        const deadline = Time.toLocalDate(current.deadline + dtDeadline)
        Time.applyTimeOffset(deadline, Time.toLocalDate(current.deadline))

        const newEnd = Time.toTimestamp(deadline)
        this.updateItemDataRegistry(hubId, newEnd, newEnd)
        this.updateDeadlineSticker(hub, undefined, newEnd)
      } else if (dtDeadline > 0 && this.isRight) {
        // Start&end here are the same, so can only increase end
        const startDate = Time.toLocalDate(current.deadline)
        const deadline = Time.toLocalDate(current.deadline + dtDeadline)
        Time.applyTimeOffset(deadline, Time.toLocalDate(current.deadline))

        const newStart = Time.toTimestamp(startDate)
        const newEnd = Time.toTimestamp(deadline)
        this.updateItemDataRegistry(hubId, newStart, newEnd)
        this.updateDeadlineSticker(hub, newStart, newEnd)
      } else if (this.isLeft && dtStartDate < 0) {
        // Start&end here are the same, so can only decrease start
        const startDate = Time.toLocalDate(current.deadline + dtStartDate)
        const deadline = Time.toLocalDate(current.deadline)
        Time.applyTimeOffset(startDate, Time.toLocalDate(current.deadline))

        const newStart = Time.toTimestamp(startDate)
        const newEnd = Time.toTimestamp(deadline)
        this.updateItemDataRegistry(hubId, newStart, newEnd)
        this.updateDeadlineSticker(hub, newStart, newEnd)
      }
    }
  }

  private get isDragging(): boolean {
    return !!this.store.state.draggingHubId
  }

  private get isLeft(): boolean {
    return this.target === 'left'
  }

  private get isRight(): boolean {
    return this.target === 'right'
  }

  private get isBoth(): boolean {
    return this.target === 'box'
  }

  private get draggedInterval(): number {
    const dx = this.currentPoint.x - this.startPoint.x
    return this.store.distanceToSnappedDistance(dx)
  }

  private getInitialRange(
    hubId = this.store.state.draggingHubId
  ): [number | undefined, number | undefined] {
    const {itemDataRegistry: registry} = this.store.state

    if (!hubId || !(hubId in registry)) {
      return [undefined, undefined]
    }

    const end = registry[hubId].deadline.end
    const start = registry[hubId].deadline.start ?? end
    if (!start || !end) {
      return [undefined, undefined]
    }

    return [start, end]
  }

  private subscribe(): void {
    document.addEventListener('mousemove', this.boxMouseMove, {capture: true})
    document.addEventListener('mouseup', this.boxMouseUp, {capture: true})
  }

  private unsubscribe(): void {
    document.removeEventListener('mousemove', this.boxMouseMove, {
      capture: true
    })
    document.removeEventListener('mouseup', this.boxMouseUp, {capture: true})
  }

  private translateTime(start: number, end: number): [number, number] {
    let startDate = new Date(start)
    let endDate = new Date(end)

    const endThresholdDate = new Date(start)
    const startThresholdDate = new Date(end)
    if (!Time.canTimeBeOnSameDate(startDate.getTime(), endDate.getTime())) {
      endThresholdDate.setDate(endThresholdDate.getDate() + 1)
      Time.applyTimeOffset(startThresholdDate, startDate)

      startThresholdDate.setDate(startThresholdDate.getDate() - 1)
      Time.applyTimeOffset(endThresholdDate, endDate)
    }

    const dt = this.draggedInterval
    if (this.isBoth) {
      start = start + dt
      end = end + dt
    } else if (this.isLeft) {
      const threshold = startThresholdDate.getTime()
      start = Math.min(start + dt, threshold)
    } else if (this.isRight) {
      const threshold = endThresholdDate.getTime()
      end = Math.max(end + dt, threshold)
    }

    if (!this.store.state.autoPlanning) {
      return [start, end]
    }

    startDate = new Date(start)
    endDate = new Date(end)

    if (this.isBoth && !Time.isSameDate(startDate, endDate)) {
      // Start date is snapped to the next working day if on weekend
      if (startDate.getDay() === 6) {
        startDate.setDate(startDate.getDate() + 2)
      } else if (startDate.getDay() === 0) {
        startDate.setDate(startDate.getDate() + 1)
      }

      if (start === end) {
        endDate = startDate // one-day task
      } else {
        let workingDays = this.originalWorkingDays
        if (Time.canTimeBeOnSameDate(start, end) && workingDays > 0) {
          workingDays -= 1
        }
        endDate = Time.addWorkingDays(startDate, workingDays)
      }
    } else if (this.isLeft && !Time.isWeekend(endDate)) {
      // Start date is snapped to the next working day if on weekend
      if (startDate.getDay() === 6) {
        startDate.setDate(startDate.getDate() + 2)
      } else if (startDate.getDay() === 0) {
        startDate.setDate(startDate.getDate() + 1)
      }
    } else if (this.isRight && !Time.isWeekend(startDate)) {
      // End date is snapped to the previous working day if on weekend
      if (endDate.getDay() === 6) {
        endDate.setDate(endDate.getDate() - 1)
      } else if (endDate.getDay() === 0) {
        endDate.setDate(endDate.getDate() - 2)
      }
    }

    return [startDate.getTime(), endDate.getTime()]
  }

  private updateItemDataRegistry(
    hubId: DataObjectId,
    start: number | undefined,
    end: number
  ): void {
    const registry = this.store.state.itemDataRegistry
    let data = registry[hubId]
    data = {...data, deadline: {...data.deadline, start, end}}
    runInAction(() => {
      this.store.state.itemDataRegistry = {...registry, [hubId]: data}
    })
  }

  private updateDeadlineSticker(
    hub: Hub,
    startDate: number | undefined,
    deadline: number
  ): void {
    void Tracking.send(TrackingEvent.ganttChangeDeadlineViaGanttChartView)
    const {history, current} = Sticker.getDeadline(hub)
    const box = Sync.get(hub)
    const tag = stickerCtl.Deadline.getSticker(box)
    Sticker.pin(tag, hub.id, hub, {
      current: {
        ...current,
        startDate,
        deadline,
        timestamp: Time.now(),
        by: globals.currentUserId
      },
      history: [...history, current]
    })

    if (Task.shouldAutoPlan(hub)) {
      GanttDraft.removeDraft(hub.id)
      GanttDraft.planDependentTasks(hub.id)
    }
  }
}
